(() => {
  angular
    .module('gm.common.user.user', [])
    .factory('btgUser', gmUserFactory);

  function gmUserFactory(
    $q,
    Upload,
    gmyAcl,
    gmApi,
    GmyProfile,
    gmProperties,
    GmyToken,
    GmyUtils,
    GmyStorage,
  ) {
    'ngInject';

    const USER_BUSINESS_FIELDS_DEFAULT_EMPTY = {
      businessDocumentSent: false,
      businessProfileValidated: false,
      businessProfileVerified: false,
      businessProfile: null
    };

    const USER_DEFAULT_EMPTY = {
      email: '',
      accountType: 'personal',
      phone: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      street: '',
      streetLine2: '',
      city: '',
      stateOrProvince: '',
      zipOrPostalCode: '',
      countryCode: '',
      snn: '',
      emailVerified: false,
      phoneVerified: false,
      profileValidated: false,
      firstDepositBonus: false
    };

    const profileWatchers = [];

    let userProfile = null;
    let userProperties = {};

    return {
      clean,
      generateDefaultProfile,
      getPendingUpdates,
      getProfile,
      getProperties,
      getProperty,
      getWealthInfo,
      hasToken,
      isAuthenticated,
      isBusinessAccount,
      isFirstSession,
      isNeedToRestore,
      isPersonalVerified,
      isSignUpComplete,
      linkWealth,
      logout,
      registerProfileWatcher,
      restore,
      setProfile,
      setProperty,
      signIn,
      signUp,
      updateAddress,
      updateDob,
      updateEmail,
      updateId,
      updateName,
      updatePoa,
      updateProfile,
      updateProfileLocally,
    };

    function clean() {
      userProfile = null;
      userProperties = {};
    }

    function generateDefaultProfile(data, accountType = 'personal') {
      userProfile = generateProfileObject(data, null, accountType);
    }

    function generateProfileObject(data, origBase, accountType) {
      const base = angular.copy(origBase || USER_DEFAULT_EMPTY);

      if (accountType === GmyProfile.PROFILE_TYPES.BUSINESS) {
        angular.extend(
          base,
          angular.copy(USER_BUSINESS_FIELDS_DEFAULT_EMPTY), { accountType }
        );
      }

      GmyUtils.mergeObjects(base, data);

      return base;
    }

    function getPendingUpdates() {
      return gmApi.get('users/profile/pendingUpdates', { auth: true });
    }

    function getProfile() {
      return userProfile;
    }

    function getProperties() {
      return angular.copy(userProperties);
    }

    function getProperty(name) {
      return userProperties[name] || null;
    }

    function getWealthInfo() {
      return gmApi.get('users/wealth', { auth: true });
    }

    function hasToken() {
      return GmyToken.hasToken();
    }

    function isAuthenticated() {
      return !!GmyToken.getToken() && !!userProfile;
    }

    function isBusinessAccount() {
      return userProfile.accountType === GmyProfile.PROFILE_TYPES.BUSINESS;
    }

    function isFirstSession() {
      return GmyStorage.getSessionItem('isFirstSession');
    }

    function isNeedToRestore() {
      return !!GmyToken.getToken() && !userProfile;
    }

    function isPersonalVerified() {
      return userProfile.profileVerified;
    }

    function isSignUpComplete() {
      return gmyAcl.isSignUpFinished(getProfile());
    }

    function linkWealth(holdingNumber, passPhrase) {
      return gmApi.post('users/linkwealth', { holdingNumber, passPhrase }, { auth: true });
    }

    function loadProperties() {
      if (!isAuthenticated()) {
        return $q((res) => { res(); });
      }

      return gmProperties
        .getProperties()
        .then((props) => {
          userProperties = props;
        });
    }

    function logout() {
      return $q((resolver) => {
        if (isAuthenticated()) {
          resolver(GmyProfile.logout());
        } else {
          resolver();
        }
      })
      .then(() => {
        GmyToken.removeToken();
        notifyProfileWatchers('user:profile:cleared');
      });
    }

    function notifyProfileWatchers(action) {
      angular.forEach(profileWatchers, (profileWatcher) => {
        profileWatcher(action);
      });
    }

    function registerProfileWatcher(callback) {
      profileWatchers.push(callback);
    }

    function restore() {
      return GmyProfile
        .refreshAuthToken()
        .then((prf) => {
          userProfile = prf;
        })
        .then(loadProperties)
        .then(() => {
          notifyProfileWatchers('user:profile:loaded');
        });
    }

    function setProfile(profile) {
      userProfile = profile;
    }

    function setProperty(name, val) {
      userProperties[name] = val;
      if (isAuthenticated()) {
        gmProperties.createOrUpdateProperty(name, val);
      }
    }

    function signIn(signInData, twoFactAuth, captcha) {
      return GmyProfile
        .signIn(signInData, twoFactAuth, captcha)
        .then((profile) => {
          if (signInData.rememberMe) {
            GmyToken.persistToken();
          }
          userProfile = profile;
        })
        .then(loadProperties)
        .then(() => {
          notifyProfileWatchers('user:profile:loaded');
        });
    }

    function signUp(email, password, accountType, captcha, alreadyHashed) {
      return GmyProfile.signUp(email, password, accountType, captcha, alreadyHashed)
        .then(() => generateDefaultProfile({ email }, accountType));
    }

    function updateAddress(address, file) {
      const data = Upload.json({ address });
      return Upload.upload({
        url: `${gmApi.getApiBasePath()}users/address`,
        data: {
          data,
          file
        },
        method: 'POST',
        headers: { 'X-Auth-Token': GmyToken.getToken() }
      });
    }

    function updateDob(dob, reason, reasonDetails, file) {
      const data = Upload.json({ dob, reason, reasonDetails, });
      return Upload.upload({
        url: `${gmApi.getApiBasePath()}users/dob`,
        data: {
          data,
          file
        },
        method: 'POST',
        headers: { 'X-Auth-Token': GmyToken.getToken() }
      });
    }

    function updateEmail(email) {
      return gmApi.post('users/email', { email }, { auth: true });
    }

    function updateId(type, file) {
      const data = Upload.json({
        idWithData: {
          type,
          number: '',
        }
      });
      return Upload.upload({
        url: `${gmApi.getApiBasePath()}users/document`,
        data: {
          data,
          file
        },
        method: 'POST',
        headers: { 'X-Auth-Token': GmyToken.getToken() }
      });
    }

    function updateName(first, last, reason, reasonDetails, file) {
      const data = Upload.json({ first, last, reason, reasonDetails, });
      return Upload.upload({
        url: `${gmApi.getApiBasePath()}users/name`,
        data: {
          data,
          file
        },
        method: 'POST',
        headers: { 'X-Auth-Token': GmyToken.getToken() }
      });
    }

    function updatePoa(file) {
      const data = Upload.json({
        idWithData: {
          type: 'ProofOfAddress',
          number: '',
        }
      });
      return Upload.upload({
        url: `${gmApi.getApiBasePath()}users/document`,
        data: {
          data,
          file
        },
        method: 'POST',
        headers: { 'X-Auth-Token': GmyToken.getToken() }
      });
    }

    function updateProfile(profile) {
      return GmyProfile.updateProfile(profile)
        .then(() => {
          userProfile = generateProfileObject(profile, userProfile, profile.accountType);
          return profile;
        });
    }

    function updateProfileLocally(profile) {
      userProfile = generateProfileObject(profile, userProfile, profile.accountType);
    }
  }
})();
